import { CheckCircle2, Clock, Headphones, MessageSquare } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { SupportTicketDto } from "@shared/types/SupportTicketDto";

interface TicketCardProps {
  ticket: SupportTicketDto;
  onClick: () => void;
}

export function TicketCard({ ticket, onClick }: TicketCardProps) {
  const messageCount = ticket.messages?.length || 0;

  return (
    <Card
      className={`glass-card border-gradient cursor-pointer hover:border-primary/50 transition-colors ${
        !ticket.isResolved ? "border-l-4 border-l-amber-400" : ""
      }`}
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${
                ticket.isResolved ? "bg-emerald-400/10" : "bg-amber-400/10"
              }`}
            >
              {ticket.isResolved ? (
                <CheckCircle2 className="h-6 w-6 text-emerald-400" />
              ) : (
                <Headphones className="h-6 w-6 text-amber-400" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono text-muted-foreground">
                  {ticket.ticketId}
                </span>
              </div>
              <h3 className="font-semibold text-lg">{ticket.ticketName}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                {ticket.ticketDescription}
              </p>
              <div className="flex items-center gap-3 mt-3">
                <Badge
                  className={
                    ticket.isResolved
                      ? "bg-emerald-400/20 text-emerald-400"
                      : "bg-amber-400/20 text-amber-400"
                  }
                >
                  {ticket.isResolved ? (
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                  ) : (
                    <Clock className="h-3 w-3 mr-1" />
                  )}
                  {ticket.isResolved ? "Resolved" : "Open"}
                </Badge>
                {ticket.isManuallyResolved && (
                  <Badge variant="secondary">Admin Resolved</Badge>
                )}
                {ticket.project && (
                  <Badge variant="outline">{ticket.project.name}</Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {messageCount > 0 && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MessageSquare className="h-4 w-4" />
                <span className="text-sm">{messageCount}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-6 mt-4 text-xs text-muted-foreground">
          <span>Created: {new Date(ticket.createdAt).toLocaleDateString()}</span>
          {ticket.resolvedAt && (
            <span>
              Resolved: {new Date(ticket.resolvedAt).toLocaleDateString()}
            </span>
          )}
          {ticket.assignee && (
            <span>
              Assigned to: {ticket.assignee.firstName}{" "}
              {ticket.assignee.lastNameAbr}.
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

